import React from 'react';
import { Sun, Moon, PanelLeft } from 'lucide-react';
import { BellIcon } from '../icons';
import { NotificationModal } from '../notifications/NotificationModal';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../ui/Breadcrumb';
import { Button } from '../ui/Button';
import { useAppContext } from '../../contexts/AppContext';
import { useNotificationsContext } from '../../contexts/NotificationsContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useBreadcrumb } from '../../hooks/useBreadcrumb';
import { usePermissions } from '../../hooks/usePermissions';
import { TrialBadge } from '../../features/billing/components/TrialBadge';
import { getTrialDaysRemaining } from '../../domain/entities/Subscription';

const BreadcrumbNav: React.FC = () => {
    const items = useBreadcrumb();
    const { setCurrentPage } = useAppContext();

    if (!items.length || !items[0].label) return null;

    return (
        <Breadcrumb>
            <BreadcrumbList>
                {items.map((item, idx) => {
                    const isLast = idx === items.length - 1;
                    return (
                        <React.Fragment key={`${item.label}-${idx}`}>
                            {idx > 0 && <BreadcrumbSeparator />}
                            <BreadcrumbItem>
                                {isLast ? (
                                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                                ) : (
                                    <BreadcrumbLink onClick={() => item.page && setCurrentPage(item.page)}>
                                        {item.label}
                                    </BreadcrumbLink>
                                )}
                            </BreadcrumbItem>
                        </React.Fragment>
                    );
                })}
            </BreadcrumbList>
        </Breadcrumb>
    );
};

type AuthenticatedHeaderProps = {
    onToggleSidebar: () => void;
};

/**
 * Chrome do app autenticado: sidebar toggle, breadcrumb, trial, tema e notificações.
 * Mobile: oculto (bottom nav + headers de página). Desktop (md+) intacto.
 */
export const AuthenticatedHeader: React.FC<AuthenticatedHeaderProps> = ({ onToggleSidebar }) => {
    const { setCurrentPage } = useAppContext();
    const { unreadCount } = useNotificationsContext();
    const { currentOrganization } = useOrganization();
    const { canViewBilling } = usePermissions();

    const [isNotificationOpen, setIsNotificationOpen] = React.useState(false);
    const bellRef = React.useRef<HTMLButtonElement>(null);

    const [theme, setTheme] = React.useState<'light' | 'dark'>(() => {
        return (document.documentElement.dataset.theme as 'light' | 'dark') || 'light';
    });

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        document.documentElement.dataset.theme = newTheme;
        document.documentElement.classList.toggle('dark', newTheme === 'dark');
        document
            .querySelectorAll('meta[name="theme-color"]')
            .forEach((el) => {
                el.setAttribute('content', newTheme === 'dark' ? '#09090E' : '#F7F7FB');
            });
    };

    const isLocalTrial =
        currentOrganization?.subscriptionStatus === 'trialing' &&
        Boolean(currentOrganization?.trialEndsAt) &&
        (currentOrganization?.trialEndsAt as Date) > new Date();

    const trialDaysLeft =
        isLocalTrial && currentOrganization?.trialEndsAt
            ? getTrialDaysRemaining(currentOrganization.trialEndsAt)
            : 0;

    return (
        <header className="hidden shrink-0 items-center justify-between border-b border-border bg-card px-4 py-3 md:flex md:px-6">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onToggleSidebar}
                    className="hidden md:inline-flex p-2 text-secondary hover:bg-surface rounded-lg transition-colors"
                    title="Alternar Menu Lateral"
                    aria-label="Alternar menu lateral"
                >
                    <PanelLeft className="w-5 h-5 shrink-0" aria-hidden />
                </button>
            </div>

            <div className="flex-1 flex items-center">
                <BreadcrumbNav />
            </div>

            <div className="flex items-center gap-3">
                {isLocalTrial && (
                    <div className="flex items-center gap-2">
                        <TrialBadge daysLeft={trialDaysLeft} totalDays={7} size="sm" />
                        {canViewBilling && (
                            <Button size="sm" variant="secondary" onClick={() => setCurrentPage('subscription')}>
                                Ver planos
                            </Button>
                        )}
                    </div>
                )}
                <button
                    type="button"
                    onClick={toggleTheme}
                    className="p-2 text-secondary hover:bg-surface rounded-full transition-colors"
                    title="Alternar Tema (Light/Dark)"
                    aria-label="Alternar tema"
                >
                    {theme === 'dark' ? (
                        <Sun className="w-5 h-5" aria-hidden />
                    ) : (
                        <Moon className="w-5 h-5" aria-hidden />
                    )}
                </button>

                <div className="relative">
                    <button
                        ref={bellRef}
                        type="button"
                        className="p-2 text-secondary hover:bg-surface rounded-full transition-colors relative"
                        title="Notificações"
                        aria-label={`Notificações${unreadCount > 0 ? `, ${unreadCount} não lidas` : ''}`}
                        data-testid="notification-bell"
                        data-unread-count={unreadCount}
                        onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                    >
                        <BellIcon className="w-5 h-5" aria-hidden />
                        {unreadCount > 0 && (
                            <span
                                className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-background"
                                data-testid="notification-unread-badge"
                                aria-hidden
                            />
                        )}
                    </button>
                    <NotificationModal
                        isOpen={isNotificationOpen}
                        onClose={() => setIsNotificationOpen(false)}
                        triggerRef={bellRef}
                    />
                </div>
            </div>
        </header>
    );
};
